import { Button } from 'ui/components/ButtonV2/Button';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import { getFormatters } from 'ui/hooks/formatter';
import { useMemo } from 'react';
import { getPriceValues } from 'ui/utils/variantHelpers';
import { AmplitudeEvents, TappedPlanCard } from 'whoop-analytics/lib/types';
import { getMembershipProperties } from '@/lib/utils/analyticsHelper';
import { addToEcommCart } from '@/actions/cartActions';
import { useToast } from 'ui/components/Toast/use-toast';
import { useAnalytics } from '@/hooks/analytics';
import { Trans, useTranslation } from '@whoop/i18n/lang/client';

export function TwoYearCartUpsell(): React.ReactNode {
  const { toast } = useToast();
  const { t } = useTranslation('cart');
  const { trackAmplitudeEvent } = useAnalytics();
  const {
    cart,
    country,
    currency,
    language,
    membershipVariants,
    setEcommCart,
  } = useSiteStore(
    useShallow((state) => ({
      cart: state.cart,
      country: state.country,
      currency: state.currency,
      language: state.language,
      membershipVariants: state.membershipVariants,
      setEcommCart: state.setEcommCart,
    })),
  );

  const { formatPrice } = getFormatters({
    currency,
    language,
    region: country,
  });

  if (!membershipVariants) return;

  const { annualMembership, twoYearMembership } = membershipVariants;
  if (!annualMembership || !twoYearMembership) return;

  const { amountSavings, addedPrice, displayPrice } = useMemo(() => {
    const annualPrices = getPriceValues(annualMembership);
    const twoYearPrices = getPriceValues(twoYearMembership);

    const rawAmountSavings =
      (annualPrices.displayPrice ?? 0) * 2 - (twoYearPrices.displayPrice ?? 0);
    const amountSavings =
      rawAmountSavings && formatPrice(rawAmountSavings, { roundUp: true });

    const rawAddedPrice =
      (twoYearPrices.displayPrice ?? 0) - (annualPrices.displayPrice ?? 0);
    const addedPrice =
      rawAddedPrice && formatPrice(rawAddedPrice, { roundUp: true });

    const displayPrice =
      annualPrices.displayPrice &&
      formatPrice(annualPrices.displayPrice, { roundUp: true });

    return { amountSavings, addedPrice, displayPrice };
  }, [annualMembership, twoYearMembership, currency, language, country]);

  const handleAddToCart = async (): Promise<void> => {
    try {
      let newCart = cart;
      trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
        ...getMembershipProperties(twoYearMembership, language),
        action: 'click',
        tapped_plan_card: TappedPlanCard.TwoYearUpsell,
      });
      newCart = await addToEcommCart({
        requestBody: {
          cart_id: newCart.id,
          country,
          locale: language,
          sku: twoYearMembership.sku,
          quantity: 1,
        },
      });
      setEcommCart(newCart);
    } catch (error) {
      toast({
        title: t('errorTitle'),
        description: t('errorDescription'),
        variant: 'error',
      });
    } finally {
      trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
        ...getMembershipProperties(twoYearMembership, language),
        action: 'success',
        tapped_plan_card: TappedPlanCard.TwoYearUpsell,
      });
    }
  };

  return (
    <div
      data-testid='two-year-cart-upsell'
      className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4'
    >
      <div className='flex flex-col gap-1'>
        <h4 className='text-base font-bold'>
          {t('cart:twoYearCartUpsell:title', { amountSavings })}
        </h4>
        <p className='text-gray-b50 text-sm font-medium leading-4'>
          {t('cart:twoYearCartUpsell:description')}
        </p>
      </div>
      <Button onClick={handleAddToCart}>
        <Trans
          i18nKey='cart:twoYearCartUpsell:CTAText'
          values={{ addedPrice, displayPrice }}
          components={{
            strikethrough: <s className='text-gray-b50 pl-0.5' />,
          }}
        />
      </Button>
    </div>
  );
}
